import React, { useEffect, forwardRef, createRef } from 'react';
import * as SliderHelper from './Slider.helper';

const inlineBlockStyle = {
  display: 'inline-block',
};

export const sapMSliderTickStyles = {
  ...inlineBlockStyle,
  height: '0.75rem',
  margin: '.375rem 0px 0px -0.0625rem',
  borderLeft: '.0625rem solid #89919a',
};
const sapMSliderTickLabel = {
  ...inlineBlockStyle,
  height: '1rem',
  position: 'absolute',
  zIndex: '1',
  width: '0.0625rem',
  margin: '0 0 0 -0.0625rem',
};
const sapMSliderLabel = {
  paddingTop: '0.375rem',
  height: '1rem',
  width: '2rem',
  left: '-1rem',
  fontSize: '.75rem',
  color: '#6a6d70',
  top: '1.2rem',
  display: 'block',
  textAlign: 'center',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  position: 'absolute',
  direction: 'ltr',
};
export const sapMSliderProgressStyles = {
  background: '#0854a0',
  height: '0.25rem',
  borderRadius: '0.25rem',
  position: 'absolute',
};
export const sapMSliderClaimsStyles = {
  width: '1.25rem',
  height: '1.25rem',
  top: '-0.6rem',
  marginLeft: '-0.8125rem',
  background: '#fff',
  border: '.125rem solid #89919a',
  borderRadius: '1rem',
  display: 'block',
  outline: 'none',
  position: 'absolute',
  cursor: 'pointer',
};
export const sapMSliderClaimsListStyle = {
  height: '1rem',
  top: '0.675rem',
  margin: '0px',
  padding: '0px',
  listStyle: 'none',
  position: 'absolute',
  zIndex: '0',
  left: '1.0625rem',
  right: '1.0625rem',
};

export const setTracerWhenMoving = (tracer, event) => {
  if (tracer.current.isMoving) {
    const newClientX = event.clientX || event.targetTouches[0].clientX;
    const differ = ((tracer.current.startClientX - newClientX) / tracer.current.totalWidth) * 100;
    const newRate = parseFloat(tracer.current.style.left) - differ;
    tracer.current.style.left = newRate + '%';
    tracer.current.startClientX = newClientX;
  }
};

export const setTracerWhenEnd = (tracer, rulers, total, onChange) => {
  if (tracer.current.isMoving) {
    const newRate = parseFloat(tracer.current.style.left);
    const [value, percentage] = SliderHelper.findNearestRuler(newRate, rulers, total);
    tracer.current.isMoving = false;
    tracer.current.style.left = percentage + '%';
    typeof onChange === 'function' &&
      onChange({
        target: {
          value,
        },
      });
  }
};

const doTouchstart = (event, stack, tracer, rulers, total, onChange) => {
  event.preventDefault();
  if (!tracer.current || !stack.current) {
    return;
  }
  tracer.current.isMoving = true;
  tracer.current.startLeft = tracer.current.style.left || 0;
  tracer.current.startClientX = event.clientX || event.targetTouches[0].clientX;
  tracer.current.totalWidth = stack.current.offsetWidth;
  setTracerWhenEnd(tracer, rulers, total, onChange);
};

const doTouchend = (event, tracer, stack, rulers, total, onChange) => {
  event.preventDefault();
  if (!tracer.current || !stack.current) {
    return;
  }
  if (tracer.current.isMoving) {
    const newRate = parseFloat(tracer.current.style.left);
    const [value, percentage] = SliderHelper.findNearestRuler(newRate, rulers, total);
    tracer.current.isMoving = false;
    tracer.current.style.left = percentage + '%';
    typeof onChange === 'function' &&
      onChange({
        target: {
          value,
        },
      });
  }
};

const doTouchmove = (event, tracer, stack) => {
  event.preventDefault();
  if (!tracer.current || !stack.current) {
    return;
  }
  setTracerWhenMoving(tracer, event);
};

const doStackClick = (event, stack, enableClick, tracer, rulers, total, onChange) => {
  if (stack.current && enableClick) {
    let currentTarget = event.currentTarget;
    let offsetLeft = currentTarget.offsetLeft;
    while (currentTarget.offsetParent) {
      offsetLeft += currentTarget.offsetParent.offsetLeft;
      currentTarget = currentTarget.offsetParent;
    }
    const offsetX = event.clientX - offsetLeft;
    const newRate = (offsetX / stack.current.offsetWidth) * 100;
    const [value, percentage] = SliderHelper.findNearestRuler(newRate, rulers, total);
    tracer.current.style.left = percentage + '%';
    typeof onChange === 'function' &&
      onChange({
        target: {
          value,
        },
      });
  }
};

export default forwardRef(
  (
    {
      total = 4,
      value = 3,
      showRuler = true,
      onChange = null,
      enableDrag = false,
      enableClick = true,
    },
    ref,
  ) => {
    const tracer = createRef();
    const stack = createRef();
    const rulers = [];
    for (let i = 0; i <= total; i++) {
      rulers.push({
        value: i,
        percentage: (i / total) * 100,
      });
    }

    const onStackClickHandler = event => {
      return doStackClick(event, stack, enableClick, tracer, rulers, total, onChange);
    };

    const touchstartHandler = event => {
      return doTouchstart(event, stack, tracer, rulers, total, onChange);
    };

    const touchendHandler = event => {
      return doTouchend(event, tracer, stack, rulers, total, onChange);
    };

    const touchmoveHandler = event => {
      return doTouchmove(event, tracer, stack);
    };

    useEffect(() => {
      const current = tracer.current;
      if (enableDrag) {
        if (current) {
          current.isMoving = false;
        }
        SliderHelper.removeDragEventListeners(
          current,
          touchstartHandler,
          touchendHandler,
          touchmoveHandler,
        );
        SliderHelper.addDragEventListeners(
          current,
          touchstartHandler,
          touchendHandler,
          touchmoveHandler,
        );
      }
      return () => {
        if (current) {
          current.isMoving = false;
        }
        SliderHelper.removeDragEventListeners(
          current,
          touchstartHandler,
          touchendHandler,
          touchmoveHandler,
        );
      };
    });

    return (
      <div
        ref={ref}
        style={{
          ...inlineBlockStyle,
          width: '100%',
          height: '2rem',
          paddingTop: '1rem',
          paddingBottom: '1rem',
          verticalAlign: 'top',
          boxSizing: 'border-box',
          position: 'relative',
          touchAction: 'pan-y',
          padding: '1.406rem 1.0625rem',
          marginBottom: '2rem !important',
          userSelect: 'none',
        }}
        data-testid={`slider-wrapper`}
      >
        <div
          ref={stack}
          onClick={onStackClickHandler}
          style={{
            width: '100%',
            height: '0.25rem',
            zIndex: 1,
            position: 'relative',
            minWidth: '4rem',
            borderRadius: '0.25rem',
            background: '#89919a',
          }}
          data-testid={`slider-stack`}
        >
          <div
            aria-hidden="true"
            className="sapMSliderProgress"
            style={{
              width: `${(value / total) * 100}%`,
              ...sapMSliderProgressStyles,
            }}
            data-testid={`slider-sapMSliderProgress`}
          />
          <div
            id={'slider_claims_' + Math.random().toFixed(4)}
            ref={tracer}
            role="slider"
            aria-orientation="horizontal"
            aria-valuemin="0"
            aria-valuemax="10"
            aria-valuenow="4"
            aria-labelledby="__text258"
            tabIndex="0"
            style={{
              left: `${(value / total) * 100}%`,
              ...sapMSliderClaimsStyles,
            }}
            data-testid={`slider-sapMSliderClaims`}
          />
        </div>
        <ul
          style={{
            ...sapMSliderClaimsListStyle,
            display: showRuler ? 'block' : 'none',
          }}
          data-testid={`slider-sapMSliderClaimsList`}
        >
          <li
            style={{
              ...sapMSliderTickLabel,
              left: '0%',
            }}
          >
            <div style={{ ...sapMSliderLabel }}>0</div>
          </li>
          {rulers.slice(1).map((ruler, index) => {
            return (
              <li
                key={ruler.value}
                style={{ ...sapMSliderTickStyles, width: `${100 / total}%` }}
                data-testid={`slider-sapMSliderTick-${index}`}
              />
            );
          })}
          <li style={{ ...sapMSliderTickLabel, left: '100%' }}>
            <div style={{ ...sapMSliderLabel }}>{total}</div>
          </li>
          <li style={{ ...sapMSliderTickStyles, width: '0%' }} />
        </ul>
      </div>
    );
  },
);
